import { Component, effect, inject, signal } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { AuthService } from 'src/app/core/auth/services/auth.service';
import { SessionTimeoutService } from 'src/app/core/auth/services/session-timeout.service';
import { CountDownComponent } from 'src/app/core/auth/shared/components/count-down/count-down.component';
import { ButtonComponent } from 'src/app/shared/ui/button/button.component';
import { ModalComponent } from 'src/app/shared/ui/modal/modal.component';
import { Constants } from 'src/environments/global-settings';

@Component({
	selector: 'app-idle-session-modal',
	templateUrl: './idle-session-modal.component.html',
	imports: [ModalComponent, CountDownComponent, ButtonComponent],
})
export class IdleSessionModalComponent {
	private authService = inject(AuthService);
	private sessionService = inject(SessionTimeoutService);

	protected readonly warningDurationSec = Constants.SESSION_TIMEOUT;
	protected isDialogVisible = signal(false);

	private readonly dialogTimer = effect((onCleanup) => {
		// This will not be fired if the timeoutDate signal value is set to the same value it was before
		const timeoutDate = this.sessionService.timeoutDate();
		const isLoggedIn = this.authService.isLoggedIn();

		// If no date is present and not logged in, ensure dialog is hidden and exit
		if (!timeoutDate || !isLoggedIn) {
			this.isDialogVisible.set(false);
			return;
		}

		// Calculate when the dialog should appear
		const expiresAt = new Date(timeoutDate).getTime();
		const showDialogAt = expiresAt - this.warningDurationSec * 1000;

		// Calculate the delay from NOW until that moment
		const now = Date.now();
		const delay = showDialogAt - now;

		// If delay is positive: We have time, wait until then.
		// If delay is negative: We are ALREADY in the warning zone (or expired), show immediately.
		const timerDelay = Math.max(0, delay);

		// If we are waiting (delay > 0), ensure dialog is hidden initially
		if (timerDelay > 0) {
			this.isDialogVisible.set(false);
		}

		// Start the single-shot timer
		const sub: Subscription = timer(timerDelay).subscribe(() => {
			this.isDialogVisible.set(true);
		});

		// Cleanup: If signal updates (session refreshed), kill the old timer
		onCleanup(() => {
			sub.unsubscribe();
		});
	});

	continueSession(): void {
		// The close dialog relays on timeoutDate() being changed from session service
		// If this is not renewed then dialog will be kept open
		this.authService.getUser().subscribe();
	}

	endSession(): void {
		this.authService.signOut();
	}
}
